// Growem - inter-exercise load-transfer map (Tier 2 of the per-exercise target resolver).
//
// WHY THIS EXISTS
// A brand-new PLAN_V2 lift (last:'' and a vague target like "progresa cada semana") has no own
// history, so the resolver cannot show a real weight. When the user HAS logged a RELATED lift
// (same muscle group + a comparable movement pattern), we can synthesize a CONSERVATIVE starting
// weight from that lift's estimated 1RM (e1RM). This file encodes, AS DATA, which related lift to
// read and the evidence-based ratio to apply. Every ratio is a documented rule of thumb, not a
// precise law: the output is framed to the user as a SUGGESTION and is deliberately rounded down
// and sized to a 2-3 RIR first attempt (new movement => technique-limited, no motor learning yet).
//
// RATIOS ARE IN e1RM SPACE. target_e1rm = source_e1rm * ratio. The resolver then inverts Epley at
// the top of the target rep range with a conservative RIR to get a working weight (see target.ts).
//
// CONFIDENCE TIERS (see the sources on each entry):
//  - HIGH: same machine/station, only the handle or foot placement changes (~1.0).
//  - MODERATE: free-weight <-> free-weight per-hand ratios, or same pattern on a different machine
//    (a haircut is applied for the load-specificity of machines).
//  - NOT ESTIMATED (absent from this map on purpose): cross-pattern within a group (hip-hinge RDL
//    from a knee-flexion leg curl), a group with zero history (glute / hip thrust), or cross-machine
//    calf work where kg is not comparable. Those fall through to the Tier-3 first-time cue, which is
//    the scientifically honest choice rather than a pseudo-precise number.

/** One candidate source lift for an estimate, tried in array order (first with history wins). */
export interface RelatedSource {
  /** Exact exercise NAME to read the user's logged top-set e1RM from (name = identity, see plan.ts). */
  name: string
  /** Multiply the source lift's e1RM by this to get the new lift's target e1RM (conservative end). */
  ratio: number
  /** Source is a bilateral / two-limb load and the new lift is single-limb: the ratio already folds
   *  the per-hand split, and the suggestion is labelled "por mano". */
  perHand?: boolean
}

export interface RelatedRule { sources: RelatedSource[] }

// newExerciseName -> ordered candidate sources. Only entries listed here get a Tier-2 estimate.
export const RELATED_ESTIMATE: Record<string, RelatedRule> = {
  // --- HIGH confidence: same machine/station, only handle or foot placement differs (~1.0) ---
  // Standard-foot leg press continues the same sled/stack as the user's high-feet / two-leg press.
  'Prensa': { sources: [
    { name: 'Prensa (pies altos)', ratio: 1.0 },
    { name: 'Prensa de piernas', ratio: 1.0 },
  ] },
  // Same pulldown station, neutral vs pronated handle: narrow/neutral grips load equal or slightly
  // more than a wide pronated grip, so a small (0.95) discount confirms on set 1.
  // Source: https://www.strengthlog.com/lat-pulldown-variations/
  'Jalón agarre neutro': { sources: [
    { name: 'Jalón al pecho', ratio: 0.95 },
  ] },
  // V1 "Curl femoral" IS the lying leg curl (its tech note: "cadera fija al banco"); essentially a
  // rename of the same movement.
  'Curl femoral tumbado': { sources: [
    { name: 'Curl femoral', ratio: 1.0 },
  ] },

  // --- MODERATE: same muscle + pattern, different variation/machine (haircut applied) ---
  // Seated leg curl loads the hamstring at a longer muscle length (hip flexed) => start ~10% under
  // the lying load. Source: https://liftvault.com/exercises/lying-leg-curl-vs-seated-leg-curl/
  'Curl femoral sentado': { sources: [
    { name: 'Curl femoral', ratio: 0.90 },
  ] },
  // Flat machine press bootstrapped from the flat multipress (~0.88, machine cam/lever caveat) or
  // from the 30 deg incline (flat ~= incline * 1.25, netted with a machine + conservatism haircut
  // to ~1.05). Sources: https://www.strengthlog.com/incline-bench-press-vs-flat-bench-press/ ;
  // machine vs free-weight specificity https://www.ncbi.nlm.nih.gov/pmc/articles/PMC10426227/
  'Press banca (máquina)': { sources: [
    { name: 'Press de banca (multipress)', ratio: 0.88 },
    { name: 'Press inclinado 30°', ratio: 1.05 },
  ] },
  // Neutral-grip machine chest press: prefer the freshest same-machine-family proxy once it exists
  // (Monday's machine press, ~0.95, shoulder-friendly so a hair lighter), else bootstrap off the
  // multipress (~0.85) or incline (~1.0). Same machine-specificity caveat.
  'Press pecho máquina agarre neutro': { sources: [
    { name: 'Press banca (máquina)', ratio: 0.95 },
    { name: 'Press de banca (multipress)', ratio: 0.85 },
    { name: 'Press inclinado 30°', ratio: 1.0 },
  ] },

  // --- MODERATE: bilateral -> single-dumbbell (ratio is PER HAND, includes the split) ---
  // Seated barbell/machine OHP -> seated neutral DB press per hand. StrengthLevel: seated DB press
  // ~= 0.54 of barbell OHP per hand; shaded DOWN to 0.42 for new movement + RIR 2-3 + delicate left
  // shoulder ("version amable"). Sources:
  // https://strengthlevel.com/strength-standards/seated-dumbbell-shoulder-press/kg ,
  // https://strengthlevel.com/strength-standards/overhead-press/kg
  'Press militar con mancuerna neutra': { sources: [
    { name: 'Press militar sentado', ratio: 0.42, perHand: true },
  ] },
  // Two-arm cable row -> one-arm DB row per hand. Bilateral deficit: one limb produces > 50% of the
  // two-limb load, but with torso support the one-arm row still sits below the bilateral stack per
  // side; ~0.55 conservative. Sources:
  // https://strengthlevel.com/strength-standards/one-arm-dumbbell-row/kg ,
  // https://strengthlevel.com/strength-standards/one-arm-seated-cable-row/kg
  'Remo con mancuerna a 1 brazo': { sources: [
    { name: 'Remo en polea baja', ratio: 0.55, perHand: true },
    { name: 'Remo con soporte al pecho', ratio: 0.60, perHand: true },
  ] },

  // --- DELIBERATELY ABSENT (fall through to the Tier-3 first-time cue) ---
  // 'Peso muerto rumano': no hip-hinge pattern exists in V1; a knee-flexion leg curl cannot anchor a
  //   compound axial hinge. Any ratio would be fabrication. Plan already: ligero, RIR 3, nunca al fallo.
  // 'Hip thrust': the 'gluteo' group has zero logged history; nearest load (leg press) is a different
  //   pattern. No reliable proxy.
  // 'Pantorrilla de pie' / 'Pantorrilla sentado (sóleo)': the calf-in-leg-press uses a sled at a
  //   totally different scale, and seated (soleus) vs standing (gastrocnemius) is a different muscle;
  //   kg is not comparable across these machines.
}
